<template>
  <ValidationProvider
    v-if="valid & visible"
    v-slot="{ errors }"
    :rules="field.rules"
    :name="field.label"
  >
    <!-- subheader -->
    <template v-if="field.type === 'subheader'">
      <div class="text-h6">{{ field.label }}</div>
    </template>

    <!-- paragraph -->
    <template v-if="field.type === 'paragraph'">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-html="field.value"></div>
    </template>

    <!-- text -->
    <template v-if="field.type === 'text'">
      <v-text-field :error-messages="errors" v-bind="props" v-on="$listeners" />
    </template>

    <!-- textarea -->
    <template v-if="field.type === 'textarea'">
      <v-textarea :error-messages="errors" v-bind="props" v-on="$listeners" />
    </template>

    <!-- select -->
    <template v-else-if="field.type === 'select'">
      <v-select
        v-if="field.style === 'dropdown'"
        :error-messages="errors"
        :items="field.items"
        :multiple="field.multiple"
        v-bind="props"
        v-on="$listeners"
      />
      <template v-else-if="field.style === 'checkbox' && field.multiple">
        <div :class="['my-2', { 'error--text': errors?.length > 0 }]">
          {{ field.label }}
        </div>
        <v-checkbox
          v-for="(item, i) in field.items"
          :key="i"
          :error-messages="errors"
          :input-value="value"
          multiple
          :hide-details="i < field.items?.length - 1"
          class="mt-2"
          v-bind="{ ...props, label: item, value: item }"
          v-on="$listeners"
          @change="$emit('input', $event)"
        />
      </template>
      <template v-else-if="field.style === 'checkbox'">
        <div :class="['my-2', { 'error--text': errors?.length > 0 }]">
          {{ field.label }}
        </div>
        <v-radio-group
          class="mt-0"
          :error-messages="errors"
          v-bind="{ ...props, label: null }"
          v-on="$listeners"
          @change="$emit('input', $event)"
        >
          <v-radio
            v-for="(item, i) in field.items"
            :key="i"
            :label="item"
            :value="item"
          />
        </v-radio-group>
      </template>
    </template>

    <!-- upload -->
    <template v-else-if="field.type === 'upload'">
      <div :class="['my-2', { 'error--text': errors?.length > 0 }]">
        {{ field.label }}
      </div>
      <file-dropzone
        :dropzone-text="$t('fresh-vue-forms.FormViewerField.upload.label')"
        :allow-multiple="field.multiple ?? false"
        :allow-image-preview="false"
        max-file-size="25MB"
        max-total-file-size="100MB"
        :error-messages="errors"
        v-bind="{ ...props, label: null }"
        v-on="$listeners"
      />
    </template>

    <slot
      name="field"
      :field="field"
      :props="props"
      :errors="errors"
      :on="$listeners"
    />
  </ValidationProvider>
</template>

<script>
import { FileDropzone } from '@freshsolutions/fresh-vue-files'

export default {
  name: 'FormViewerField',

  components: {
    FileDropzone
  },

  props: {
    value: {
      type: null,
      required: true
    },

    /**
     * Structure:
     * {
     *   type: 'text',
     *   label: 'some_label_text',
     *   model: 'some_unique_name',
     *   rules: 'required|email',
     *   prependIcon: 'mdi-email',
     *   disabled: false,
     *   loading: false
     * }
     *
     * {
     *   type: 'select',
     *   label: 'some_label_text',
     *   model: 'some_unique_name',
     *   items: [
     *     {
     *       value: 0
     *       text: 'Select 0'
     *     }
     *   ],
     *   rules: 'required',
     *   prependIcon: 'mdi-pencil',
     *   disabled: false,
     *   loading: false
     * }
     */
    field: {
      type: Object,
      required: true
    }
  },

  computed: {
    props() {
      return {
        ...this.$attrs,
        value: this.value,
        label: this.field.label,
        'prepend-icon': this.field.prependIcon,
        disabled: this.field.disabled,
        loading: this.field.loading || !!this.field.progress
      }
    },

    valid() {
      if (!this.field.type) {
        return false
      }

      if (
        !['subheader', 'paragraph'].includes(this.field.type) &&
        !this.field.model
      ) {
        return false
      }

      // if we reach this, then everything is valid
      return true
    },

    visible() {
      return this.field && (this.field.visible || true)
    }
  }
}
</script>
